/* eslint-disable no-param-reassign, no-void */
// "3 vitórias decisivas": one rAF clock per visit (the cycles restart at t = 0 every time the grid comes
// back into view), a 500 ms data tick, per-frame easing for what moves:
//   card 1 — mirrors the Multiconexão card below (window.__npMcPlace + "np:mc-place"): the same five
//            routes placed by the visitor's location and the same five scenarios. The leader reads 2–5 ms,
//            the direct line 180–250 ms, the pill is the live delta of the two numbers (~−98%); the
//            slowest route reads red and the leader flashes when a scenario hands over.
//   card 2 — a 9 s boost cycle: CPU, GPU and RAM switch on 0.6 s apart and the FPS with boost climbs from
//            the plain figure to ×2.1 (never more than +110%).
//   card 3 — an 8 s failover cycle: 5G wavers, drops, the satellite link takes over with 0 drops, 5G
//            comes back.
// Off screen the panels settle on their resting state; reduced motion shows it from the start.
import { clamp, inOut, rint, type SimHandle } from './hero-sim';
import { type McPlace, mcPlaceHost, type McScenario } from './multiconnection';
import type { WinsStrings } from './strings';

const P_SCALE = 250;
const P_IDS = ['r1', 'r2', 'r3', 'r4', 'r5'];
const DEFAULT_PLACE: { codes: string[]; scenarios: McScenario[] } = {
  codes: ['SAO', 'RIO', 'BUE', 'SCL', 'MIA'],
  scenarios: [
    { r1: 3, r2: 5, r3: 15, r4: 20, r5: 33, bad: 214 },
    { r1: 8, r2: 3, r3: 14, r4: 21, r5: 32, bad: 226 },
    { r1: 2, r2: 4, r3: 16, r4: 19, r5: 34, bad: 197 },
    { r1: 9, r2: 2, r3: 15, r4: 20, r5: 31, bad: 238 },
    { r1: 3, r2: 5, r3: 14, r4: 21, r5: 33, bad: 205 },
  ],
};
const F_T = 9;
const KEYS = [1.0, 1.6, 2.2];
const DOWN = 3;
const BACKUP = 5;
const I_T = 8;

export function initWinsSim(section: HTMLElement, grid: HTMLElement, options: { paused: boolean; strings: WinsStrings }): SimHandle {
  const all = <T extends Element>(selector: string, scope: ParentNode = grid) => Array.from(scope.querySelectorAll<T>(selector));
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const cards = all<HTMLElement>('.win');
  const c01 = (v: number) => clamp(v, 0, 1);

  /* card 1 */
  const pOff = all<HTMLElement>('[data-sim-off]');
  const pOn = all<HTMLElement>('[data-sim-on]');
  const pChip = all<HTMLElement>('[data-sim-chip]');
  const pBar = all<HTMLElement>('[data-sim-bar]');
  const pOffBar = all<HTMLElement>('[data-sim-offbar]');
  const hops = all<HTMLElement>('.hops:not(.hops--tags) li').map((li) => ({ li, code: li.firstChild && li.firstChild.nodeType === Node.TEXT_NODE ? li.firstChild : null, value: li.querySelector<HTMLElement>('b') }));
  let pPlace = DEFAULT_PLACE;
  let pK = 0;
  let pSem = 214;
  let pWin = -1;
  function pingPaint(isLive: boolean) {
    const sc = pPlace.scenarios[pK % pPlace.scenarios.length]!;
    let win = 0;
    let slow = 0;
    P_IDS.forEach((id, i) => {
      if (sc[id]! < sc[P_IDS[win]!]!) win = i;
      if (sc[id]! > sc[P_IDS[slow]!]!) slow = i;
    });
    const w = isLive ? rint(2, 5) : clamp(sc[P_IDS[win]!]!, 2, 5);
    const vals = P_IDS.map((id, i) => (i === win ? w : Math.max(w + 1, sc[id]! + (isLive ? rint(-1, 1) : 0))));
    vals[slow] = Math.max(...vals);
    /* a walk pulled to the middle of the band */
    pSem = isLive ? clamp(pSem + rint(-18, 18) + Math.round((215 - pSem) * 0.2), 180, 250) : clamp(sc.bad || pSem, 180, 250);
    const chip = Math.round((1 - w / pSem) * 100);
    pOff.forEach((el) => {
      el.textContent = String(pSem);
    });
    pOn.forEach((el) => {
      el.textContent = String(w);
    });
    pChip.forEach((el) => {
      el.textContent = `−${chip}%`;
    });
    pOffBar.forEach((el) => el.style.setProperty('--w', `${((pSem / P_SCALE) * 100).toFixed(1)}%`));
    pBar.forEach((el) => el.style.setProperty('--w', `${((w / P_SCALE) * 100).toFixed(1)}%`));
    hops.forEach((hop, i) => {
      if (!hop.value || vals[i] == null) return;
      if (hop.code && pPlace.codes[i]) hop.code.nodeValue = pPlace.codes[i]!;
      hop.value.textContent = String(vals[i]);
      hop.li.classList.toggle('is-best', i === win);
      hop.li.classList.toggle('is-spike', i === slow && slow !== win);
    });
    if (isLive && pWin >= 0 && win !== pWin && hops[win]) {
      const { li } = hops[win]!;
      hops.forEach((hop) => hop.li.classList.remove('is-flash'));
      void li.offsetWidth;
      li.classList.add('is-flash');
    }
    pWin = win;
  }
  function pingPlace(place: McPlace | undefined) {
    if (!place || !place.routes || place.routes.length < 5 || !place.scenarios || !place.scenarios.length) return;
    pPlace = {
      codes: place.routes.slice(0, 5).map((route) =>
        String(route.code || route.city || '')
          .slice(0, 4)
          .toUpperCase()
      ),
      scenarios: place.scenarios,
    };
    pK = 0;
    pWin = -1;
    pingPaint(false);
  }
  const onPlace = (event: Event) => pingPlace((event as CustomEvent<McPlace>).detail);
  window.addEventListener('np:mc-place', onPlace);
  // eslint-disable-next-line no-underscore-dangle -- the name is the design's page-level contract
  pingPlace(mcPlaceHost().__npMcPlace);

  /* card 2 */
  const fRoot = grid.querySelector<HTMLElement>('[data-fps]');
  const fOff = grid.querySelector<HTMLElement>('[data-fps-off]');
  const fOn = grid.querySelector<HTMLElement>('[data-fps-on]');
  const fChip = grid.querySelector<HTMLElement>('[data-fps-chip]');
  const fBad = all<HTMLElement>('.fps__side--bad .fps__bars i', fRoot || grid);
  const fGood = all<HTMLElement>('.fps__side--good .fps__bars i', fRoot || grid);
  const tags = all<HTMLElement>('.hops--tags li');
  let base = 78;
  let noise = fGood.map(() => Math.random());
  function fpsTick() {
    base = clamp(base + (Math.random() - 0.5) * 3, 74, 82);
    fBad.forEach((el) => el.style.setProperty('--h', `${(22 + Math.random() * 20).toFixed(0)}%`));
    noise = fGood.map(() => Math.random());
  }
  function fpsFrame(t: number) {
    const lt = t % F_T;
    const kp = KEYS.map((key) => c01((lt - key) / 0.45));
    const frac = (kp[0]! + kp[1]! + kp[2]!) / 3;
    const b = Math.round(base);
    const g = Math.floor(b * 2.1);
    const value = Math.round(b + (g - b) * inOut(frac));
    if (fOff) fOff.textContent = String(b);
    if (fOn) fOn.textContent = String(value);
    if (fChip) {
      fChip.textContent = `+${Math.round((value / b - 1) * 100)}%`;
      fChip.classList.toggle('is-muted', frac < 0.2);
    }
    const e = inOut(frac);
    fGood.forEach((el, i) => {
      const lo = 22 + noise[i]! * 20;
      const hi = 84 + noise[i]! * 14;
      el.style.setProperty('--h', `${(lo + (hi - lo) * e).toFixed(0)}%`);
    });
    tags.forEach((li, i) => {
      const k = kp[i] == null ? 1 : kp[i]!;
      li.classList.toggle('is-idle', k <= 0);
      li.classList.toggle('is-busy', k > 0 && k < 1);
    });
    if (fRoot) fRoot.style.opacity = String(0.35 + 0.65 * c01(Math.min(lt / 0.3, (F_T - lt) / 0.5)));
  }

  /* card 3 */
  const iRoot = grid.querySelector<HTMLElement>('[data-isp]');
  const iMsg = grid.querySelector<HTMLElement>('[data-isp-msg]');
  const iItems = all<HTMLElement>('.isp__i', iRoot || grid);
  const iTxt = iMsg?.querySelector('span') ?? null;
  let iPhase = -1;
  function ispFrame(t: number) {
    const lt = t % I_T;
    /* ok · wavering · down · back */
    let ph = 0;
    if (lt >= 2.4 && lt < 3.2) ph = 1;
    else if (lt >= 3.2 && lt < 5.6) ph = 2;
    else if (lt >= 5.6 && lt < 6.3) ph = 3;
    if (ph === iPhase) return;
    iPhase = ph;
    iItems.forEach((el, i) => {
      el.classList.toggle('is-unstable', i === DOWN && (ph === 1 || ph === 3));
      el.classList.toggle('is-down', i === DOWN && ph === 2);
      el.classList.toggle('is-takeover', i === BACKUP && ph === 2);
    });
    iMsg?.classList.toggle('is-alert', ph === 2);
    if (iTxt) iTxt.textContent = ph === 2 ? options.strings.ispDown : options.strings.ispOk;
    cards[2]?.classList.toggle('is-alert', ph === 1 || ph === 2 || ph === 3);
  }

  let raf = 0;
  let last = 0;
  let t = 0;
  let acc = 0;
  let visible = false;
  let nextSwap = 5;
  let { paused } = options;
  function frame(now: number) {
    raf = 0;
    const dt = Math.min(0.064, (now - (last || now)) / 1000);
    last = now;
    t += dt;
    acc += dt;
    if (acc >= 0.5) {
      acc = 0;
      pingPaint(true);
      fpsTick();
    }
    if (t >= nextSwap) {
      pK = (pK + 1) % pPlace.scenarios.length;
      pingPaint(true);
      nextSwap = t + 5;
    }
    fpsFrame(t);
    ispFrame(t);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    schedule();
  }
  function schedule() {
    if (!raf && visible && !paused && !document.hidden) raf = requestAnimationFrame(frame);
  }
  function stop() {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    last = 0;
  }
  function restart() {
    t = 0;
    acc = 0;
    nextSwap = 5;
    iPhase = -1;
  }
  /* off screen the panels settle on their resting state (boost applied, all six online) */
  function settle() {
    fpsFrame(F_T - 0.6);
    iPhase = -1;
    ispFrame(0);
  }
  section.classList.toggle('is-paused', paused);
  const onVisibility = () => {
    if (document.hidden) stop();
    else schedule();
  };
  document.addEventListener('visibilitychange', onVisibility);
  if (reduced.matches) grid.classList.add('is-in');
  let observer: IntersectionObserver | null = null;
  if (typeof IntersectionObserver !== 'function') {
    grid.classList.add('is-in');
    visible = true;
    schedule();
  } else {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !visible) {
            visible = true;
            grid.classList.add('is-in');
            restart();
            schedule();
          } else if (!entry.isIntersecting && visible) {
            visible = false;
            stop();
            settle();
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(grid);
  }

  return {
    setPaused(next) {
      paused = next;
      section.classList.toggle('is-paused', paused);
      if (paused) stop();
      else schedule();
    },
    destroy() {
      stop();
      observer?.disconnect();
      window.removeEventListener('np:mc-place', onPlace);
      document.removeEventListener('visibilitychange', onVisibility);
    },
  };
}
